import type {
  ChartVisualisation,
  WidgetType
} from "../../../components/widgets/types.ts"
import {
  REPORT_COMPARE_OVERRIDES,
  REPORT_FILTER_DIMENSIONS,
  REPORT_WINDOW_PRESETS,
  type ReportCompareOverride,
  type ReportCompareValue,
  type ReportDefinitionV1,
  type ReportLayoutItem,
  type ReportWidgetEntry,
  type ReportWidgetFilters,
  type ReportWindow,
  type ReportWindowPreset,
  WIDGET_SIZE_PRESETS,
  type WidgetSizePreset
} from "../types/canvas.types.ts"

const GRID_COLUMNS = 12
const COLLAPSED_HEIGHT = 1
const DEFAULT_SIZE_PRESET: WidgetSizePreset = "medium"

const DEFAULT_REPORT_WINDOW: ReportWindow = { preset: "this_month" }

const CHART_VISUALISATIONS: readonly ChartVisualisation[] = [
  "line",
  "area",
  "bar",
  "composed",
  "donut",
  "pie",
  "stacked"
]

const PRESET_DIMENSIONS: Record<WidgetSizePreset, { w: number; h: number }> = {
  small: { w: 3, h: 2 },
  medium: { w: 6, h: 3 },
  large: { w: 8, h: 5 },
  full: { w: 12, h: 5 }
}

// Charts need more vertical room than compact tiles for their axis labels and
// legend, but the mockup runs shorter than the dashboard's ~288px plot height.
const CHART_PRESET_DIMENSIONS: Record<
  WidgetSizePreset,
  { w: number; h: number }
> = {
  small: { w: 4, h: 5 },
  medium: { w: 6, h: 6 },
  large: { w: 8, h: 7 },
  full: { w: 12, h: 8 }
}

// Row-based widgets scroll inside the tile, so their presets have to clear the
// card header and show several rows rather than one clipped one.
const ROW_PRESET_DIMENSIONS: Record<
  WidgetSizePreset,
  { w: number; h: number }
> = {
  small: { w: 4, h: 6 },
  medium: { w: 6, h: 8 },
  large: { w: 8, h: 10 },
  full: { w: 12, h: 12 }
}

const ROW_WIDGET_TYPES: readonly WidgetType[] = [
  "table",
  "list",
  "feed",
  "alert"
]

const WIDGET_MIN_SIZE: Partial<Record<WidgetType, { w: number; h: number }>> = {
  kpi: { w: 3, h: 3 },
  progress: { w: 3, h: 3 },
  chart: { w: 4, h: 5 },
  table: { w: 4, h: 5 },
  list: { w: 3, h: 5 },
  feed: { w: 3, h: 5 },
  alert: { w: 3, h: 5 }
}

const DEFAULT_MIN_SIZE = { w: 3, h: 3 }

// A comparing KPI stacks a second delta row (compare badge + "vs <window>"
// label) under the value, and that row wraps once the tile gets narrow. Three
// rows leaves the card body shorter than its content, which scrolls the badge
// out of view mid-chip; four rows clears the tallest wrap.
const KPI_COMPARE_MIN_HEIGHT = 4

const minSizeFor = (
  type?: WidgetType,
  comparing = false
): { w: number; h: number } => {
  const base = (type && WIDGET_MIN_SIZE[type]) || DEFAULT_MIN_SIZE
  if (!comparing || type !== "kpi") return base
  return { ...base, h: Math.max(base.h, KPI_COMPARE_MIN_HEIGHT) }
}

const presetDimensionsFor = (
  type?: WidgetType
): Record<WidgetSizePreset, { w: number; h: number }> => {
  if (type === "chart") return CHART_PRESET_DIMENSIONS
  if (type && ROW_WIDGET_TYPES.includes(type)) return ROW_PRESET_DIMENSIONS
  return PRESET_DIMENSIONS
}

const presetToSize = (
  preset: WidgetSizePreset,
  type?: WidgetType
): { w: number; h: number } => {
  const table = presetDimensionsFor(type)
  const base = table[preset] ?? table[DEFAULT_SIZE_PRESET]
  const min = minSizeFor(type)
  return { w: Math.max(base.w, min.w), h: Math.max(base.h, min.h) }
}

const isValidPreset = (value: unknown): value is WidgetSizePreset =>
  typeof value === "string" &&
  (WIDGET_SIZE_PRESETS as readonly string[]).includes(value)

const layoutOverlap = (a: ReportLayoutItem, b: ReportLayoutItem): boolean =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y

const findFreeSlot = (
  layout: ReportLayoutItem[],
  w: number,
  h: number,
  columns = GRID_COLUMNS
): { x: number; y: number } => {
  const clampedW = Math.min(w, columns)
  let y = 0
  // Bounded by the tallest existing item; every row past it is free by definition.
  const maxIterations =
    layout.reduce((acc, item) => Math.max(acc, item.y + item.h), 0) + h + 1
  while (y <= maxIterations) {
    for (let x = 0; x <= columns - clampedW; x += 1) {
      const candidate: ReportLayoutItem = {
        i: "__probe__",
        x,
        y,
        w: clampedW,
        h
      }
      if (!layout.some((item) => layoutOverlap(candidate, item)))
        return { x, y }
    }
    y += 1
  }
  return { x: 0, y }
}

const normalizeWidgetFilters = (
  raw: unknown
): ReportWidgetFilters | undefined => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined
  const obj = raw as Record<string, unknown>
  const filters: ReportWidgetFilters = {}
  for (const dimension of REPORT_FILTER_DIMENSIONS) {
    const values = obj[dimension]
    if (
      Array.isArray(values) &&
      values.every((value) => typeof value === "string")
    )
      filters[dimension] = [...values]
  }
  return filters
}

const normalizeWidgetCompare = (
  raw: unknown
): ReportCompareOverride | undefined =>
  typeof raw === "string" &&
  raw !== "inherit" &&
  (REPORT_COMPARE_OVERRIDES as readonly string[]).includes(raw)
    ? (raw as ReportCompareOverride)
    : undefined

const normalizeVisualisation = (
  raw: unknown
): ChartVisualisation | undefined =>
  typeof raw === "string" &&
  (CHART_VISUALISATIONS as readonly string[]).includes(raw)
    ? (raw as ChartVisualisation)
    : undefined

const normalizeWidgetEntry = (raw: unknown): ReportWidgetEntry | null => {
  if (!raw || typeof raw !== "object") return null
  const obj = raw as Record<string, unknown>
  const id = typeof obj.id === "string" ? obj.id : null
  const spec = typeof obj.spec === "string" ? obj.spec : null
  if (!id || !spec) return null
  const title =
    typeof obj.title === "string" && obj.title.trim() ? obj.title : undefined
  const size = isValidPreset(obj.size) ? obj.size : DEFAULT_SIZE_PRESET
  const collapsed = obj.collapsed === true
  const expandedH =
    typeof obj.expandedH === "number" &&
    Number.isFinite(obj.expandedH) &&
    obj.expandedH > COLLAPSED_HEIGHT
      ? obj.expandedH
      : undefined
  const filters = normalizeWidgetFilters(obj.filters)
  const compare = normalizeWidgetCompare(obj.compare)
  const visualisation = normalizeVisualisation(obj.visualisation)
  return {
    id,
    spec,
    title,
    size,
    collapsed,
    expandedH,
    filters,
    compare,
    visualisation
  }
}

const normalizeReportWindow = (raw: unknown): ReportWindow | undefined => {
  if (!raw || typeof raw !== "object") return undefined
  const obj = raw as Record<string, unknown>
  if (
    typeof obj.preset === "string" &&
    (REPORT_WINDOW_PRESETS as readonly string[]).includes(obj.preset)
  )
    return { preset: obj.preset as ReportWindowPreset }
  if (typeof obj.from === "string" && typeof obj.to === "string")
    return { from: obj.from, to: obj.to }
  return undefined
}

const normalizeReportCompare = (raw: unknown): ReportCompareValue => {
  if (raw === "previous_period" || raw === "same_period_last_year") return raw
  return null
}

const resolveEffectiveCompare = (
  override: ReportCompareOverride | undefined,
  reportCompare: ReportCompareValue
): ReportCompareValue => {
  if (override && override !== "inherit")
    return override === "none" ? null : override
  return reportCompare
}

const normalizeLayoutItem = (raw: unknown): ReportLayoutItem | null => {
  if (!raw || typeof raw !== "object") return null
  const obj = raw as Record<string, unknown>
  const i = typeof obj.i === "string" ? obj.i : null
  if (!i) return null
  const num = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? value : fallback
  return {
    i,
    x: num(obj.x, 0),
    y: num(obj.y, 0),
    w: num(obj.w, PRESET_DIMENSIONS[DEFAULT_SIZE_PRESET].w),
    h: num(obj.h, PRESET_DIMENSIONS[DEFAULT_SIZE_PRESET].h)
  }
}

const normalizeDefinition = (raw: unknown): ReportDefinitionV1 => {
  const obj =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const rawWidgets = Array.isArray(obj.widgets) ? obj.widgets : []
  const rawLayout = Array.isArray(obj.layout) ? obj.layout : []
  const widgets = rawWidgets
    .map(normalizeWidgetEntry)
    .filter((w): w is ReportWidgetEntry => w !== null)
  const layoutById = new Map(
    rawLayout
      .map(normalizeLayoutItem)
      .filter((l): l is ReportLayoutItem => l !== null)
      .map((l) => [l.i, l])
  )
  const layout: ReportLayoutItem[] = []
  for (const widget of widgets) {
    const existing = layoutById.get(widget.id)
    if (existing) {
      layout.push(existing)
      continue
    }
    const { w, h } = presetToSize(widget.size)
    const slot = findFreeSlot(layout, w, h)
    layout.push({ i: widget.id, ...slot, w, h })
  }
  const filters = normalizeWidgetFilters(obj.filters) ?? {}
  const window = normalizeReportWindow(obj.window)
  const compare = normalizeReportCompare(obj.compare)
  const icon =
    typeof obj.icon === "string" && obj.icon.trim() ? obj.icon : undefined
  return {
    version: 1,
    widgets,
    layout,
    filters,
    ...(window ? { window } : {}),
    ...(compare ? { compare } : {}),
    ...(icon ? { icon } : {})
  }
}

const usedSpecKeys = (
  definition: ReportDefinitionV1 | null | undefined
): Set<string> => new Set((definition?.widgets ?? []).map((w) => w.spec))

// The library offers each catalog spec once; a second copy of the same widget
// only ever comes from the explicit per-tile Duplicate action.
const addWidget = (
  definition: ReportDefinitionV1,
  args: { id: string; spec: string; type?: WidgetType }
): ReportDefinitionV1 => {
  if (definition.widgets.some((w) => w.spec === args.spec)) return definition
  const { w, h } = presetToSize(DEFAULT_SIZE_PRESET, args.type)
  const slot = findFreeSlot(definition.layout, w, h)
  const entry: ReportWidgetEntry = {
    id: args.id,
    spec: args.spec,
    size: DEFAULT_SIZE_PRESET,
    collapsed: false
  }
  const layoutItem: ReportLayoutItem = { i: args.id, ...slot, w, h }
  return {
    ...definition,
    widgets: [...definition.widgets, entry],
    layout: [...definition.layout, layoutItem]
  }
}

const duplicateWidget = (
  definition: ReportDefinitionV1,
  id: string,
  newId: string,
  type?: WidgetType
): ReportDefinitionV1 => {
  const source = definition.widgets.find((w) => w.id === id)
  const sourceLayout = definition.layout.find((l) => l.i === id)
  if (!source || !sourceLayout) return definition
  const w = sourceLayout.w
  const h = source.collapsed
    ? presetToSize(source.size, type).h
    : sourceLayout.h
  const slot = findFreeSlot(definition.layout, w, h)
  const entry: ReportWidgetEntry = {
    ...source,
    id: newId,
    filters: source.filters
      ? Object.fromEntries(
          Object.entries(source.filters).map(([dimension, values]) => [
            dimension,
            [...values]
          ])
        )
      : undefined
  }
  const layoutItem: ReportLayoutItem = { i: newId, ...slot, w, h }
  return {
    ...definition,
    widgets: [...definition.widgets, entry],
    layout: [...definition.layout, layoutItem]
  }
}

const removeWidget = (
  definition: ReportDefinitionV1,
  id: string
): ReportDefinitionV1 => ({
  ...definition,
  widgets: definition.widgets.filter((w) => w.id !== id),
  layout: definition.layout.filter((l) => l.i !== id)
})

const mapWidget = (
  definition: ReportDefinitionV1,
  id: string,
  update: (widget: ReportWidgetEntry) => ReportWidgetEntry
): ReportDefinitionV1 => ({
  ...definition,
  widgets: definition.widgets.map((w) => (w.id === id ? update(w) : w))
})

const mapLayoutItem = (
  definition: ReportDefinitionV1,
  id: string,
  update: (item: ReportLayoutItem) => ReportLayoutItem
): ReportDefinitionV1 => ({
  ...definition,
  layout: definition.layout.map((l) => (l.i === id ? update(l) : l))
})

const setWidgetTitle = (
  definition: ReportDefinitionV1,
  id: string,
  title: string | undefined
): ReportDefinitionV1 =>
  mapWidget(definition, id, (w) => ({
    ...w,
    title: title && title.trim() ? title.trim() : undefined
  }))

const setWidgetFilters = (
  definition: ReportDefinitionV1,
  id: string,
  filters: ReportWidgetFilters | undefined
): ReportDefinitionV1 => mapWidget(definition, id, (w) => ({ ...w, filters }))

const setWidgetCompare = (
  definition: ReportDefinitionV1,
  id: string,
  compare: ReportCompareOverride
): ReportDefinitionV1 =>
  mapWidget(definition, id, (w) => ({
    ...w,
    compare: compare === "inherit" ? undefined : compare
  }))

const setWidgetVisualisation = (
  definition: ReportDefinitionV1,
  id: string,
  visualisation: ChartVisualisation | undefined
): ReportDefinitionV1 =>
  mapWidget(definition, id, (w) => ({ ...w, visualisation }))

const setReportFilters = (
  definition: ReportDefinitionV1,
  filters: ReportWidgetFilters
): ReportDefinitionV1 => ({ ...definition, filters })

const setReportWindow = (
  definition: ReportDefinitionV1,
  window: ReportWindow
): ReportDefinitionV1 => ({ ...definition, window })

const setReportCompare = (
  definition: ReportDefinitionV1,
  compare: ReportCompareValue
): ReportDefinitionV1 => {
  if (!compare) {
    const { compare: _dropped, ...rest } = definition
    return rest
  }
  return { ...definition, compare }
}

const setReportIcon = (
  definition: ReportDefinitionV1,
  icon: string | undefined
): ReportDefinitionV1 => {
  if (!icon) {
    const { icon: _dropped, ...rest } = definition
    return rest
  }
  return { ...definition, icon }
}

const setWidgetSize = (
  definition: ReportDefinitionV1,
  id: string,
  size: WidgetSizePreset,
  type?: WidgetType
): ReportDefinitionV1 => {
  const { w, h } = presetToSize(size, type)
  const withSize = mapWidget(definition, id, (widget) => ({
    ...widget,
    size,
    expandedH: widget.collapsed ? h : widget.expandedH
  }))
  const widget = withSize.widgets.find((w) => w.id === id)
  if (!widget) return withSize
  return mapLayoutItem(withSize, id, (item) => ({
    ...item,
    w,
    h: widget.collapsed ? COLLAPSED_HEIGHT : h
  }))
}

const toggleCollapseWidget = (
  definition: ReportDefinitionV1,
  id: string,
  type?: WidgetType
): ReportDefinitionV1 => {
  const widget = definition.widgets.find((w) => w.id === id)
  if (!widget) return definition
  const collapsed = !widget.collapsed
  const layoutItem = definition.layout.find((item) => item.i === id)
  const expandedH = collapsed ? layoutItem?.h : widget.expandedH
  const withFlag = mapWidget(definition, id, (w) => ({
    ...w,
    collapsed,
    expandedH
  }))
  const h = collapsed
    ? COLLAPSED_HEIGHT
    : (widget.expandedH ?? presetToSize(widget.size, type).h)
  return mapLayoutItem(withFlag, id, (item) => ({ ...item, h }))
}

const updateWidgetLayoutItem = (
  definition: ReportDefinitionV1,
  id: string,
  patch: Partial<Pick<ReportLayoutItem, "x" | "y" | "w" | "h">>
): ReportDefinitionV1 =>
  mapLayoutItem(definition, id, (item) => ({ ...item, ...patch }))

const applyLayoutChange = (
  definition: ReportDefinitionV1,
  changed: readonly ReportLayoutItem[]
): ReportDefinitionV1 => {
  const byId = new Map(changed.map((item) => [item.i, item]))
  return {
    ...definition,
    layout: definition.layout.map((item) => {
      const next = byId.get(item.i)
      return next
        ? { i: item.i, x: next.x, y: next.y, w: next.w, h: next.h }
        : item
    })
  }
}

export {
  GRID_COLUMNS,
  COLLAPSED_HEIGHT,
  KPI_COMPARE_MIN_HEIGHT,
  DEFAULT_SIZE_PRESET,
  DEFAULT_REPORT_WINDOW,
  PRESET_DIMENSIONS,
  CHART_PRESET_DIMENSIONS,
  WIDGET_MIN_SIZE,
  DEFAULT_MIN_SIZE,
  minSizeFor,
  presetToSize,
  findFreeSlot,
  normalizeDefinition,
  resolveEffectiveCompare,
  usedSpecKeys,
  addWidget,
  duplicateWidget,
  removeWidget,
  setWidgetTitle,
  setWidgetFilters,
  setWidgetCompare,
  setWidgetVisualisation,
  setReportFilters,
  setReportWindow,
  setReportCompare,
  setReportIcon,
  setWidgetSize,
  toggleCollapseWidget,
  updateWidgetLayoutItem,
  applyLayoutChange
}
